// Company skills — live view of Dave's mod-skills registry (GitLab → Bedrock
// AgentCore + s3://mod-skills mirror). Read-only: browse, copy, install snippet.
import { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { EmptyState, SectionCard, StatusPill, Icon } from "../../mui";
import { relDate } from "../../mui/format";
import { listCompanySkills, getCompanySkillFile, type CompanySkill } from "../../lib/brain-api";

const c = brainTokens.color;
const REPO_URL = "https://gitlab.com/aeronetsoftware/devops/mod-skills";

export function CompanySkillsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [skills, setSkills] = useState<CompanySkill[] | null>(null);
  const [selected, setSelected] = useState<CompanySkill | null>(null);
  const [activeFile, setActiveFile] = useState("SKILL.md");
  const [content, setContent] = useState("");

  useEffect(() => {
    listCompanySkills()
      .then(setSkills)
      .catch(() => {
        setSkills([]);
        enqueueSnackbar("Failed to load the skills registry.", { variant: "error" });
      });
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!selected) return;
    setContent("");
    getCompanySkillFile(selected.name, activeFile)
      .then((r) => setContent(r.content))
      .catch(() => setContent("(failed to load)"));
  }, [selected, activeFile]);

  const copy = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied.`, { variant: "success" });
  };

  if (skills === null) return <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading skills registry…</Box>;

  if (selected) {
    // Fetch every file the manifest lists, not just SKILL.md — companion files matter.
    const files = selected.files.length ? selected.files : ["SKILL.md"];
    const installCmd = `mkdir -p ~/.claude/skills/${selected.name} && for f in ${files.join(" ")}; do curl -sf --create-dirs "${REPO_URL}/-/raw/main/.claude/skills/${selected.name}/$f" -o ~/.claude/skills/${selected.name}/$f; done && echo "installed ${selected.name} (${files.length} file${files.length === 1 ? "" : "s"})"`;
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Button size="small" onClick={() => { setSelected(null); setActiveFile("SKILL.md"); }} startIcon={<Icon name="arrow-left" size={14} stroke={1.8} />}>
            Back
          </Button>
          <Box sx={{ fontSize: 17, fontWeight: 600, flex: 1, fontFamily: brainTokens.font.mono }}>{selected.name}</Box>
          {selected.syncedAt && <StatusPill tone="teal" label={`synced ${relDate(Date.parse(selected.syncedAt))}`} />}
          {selected.gitSha && (
            <Box sx={{ fontSize: 11.5, color: c.faint, fontFamily: brainTokens.font.mono }}>{selected.gitSha.slice(0, 7)}</Box>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Button size="small" variant="contained" onClick={() => void copy(content, "Skill")}>
            Copy {activeFile}
          </Button>
          <Button size="small" variant="outlined" onClick={() => void copy(installCmd, "Install command")}>
            Copy install command
          </Button>
          <Button size="small" component="a" href={`${REPO_URL}/-/tree/main/.claude/skills/${selected.name}`} target="_blank">
            Open in GitLab
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Box sx={{ width: 200, flex: "none" }}>
            {selected.files.map((f) => (
              <Box
                key={f}
                onClick={() => setActiveFile(f)}
                sx={{
                  fontSize: 12.5,
                  p: "6px 8px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  bgcolor: activeFile === f ? c.panelBg : "transparent",
                  fontFamily: brainTokens.font.mono,
                  mb: 0.5,
                }}
              >
                {f}
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              whiteSpace: "pre-wrap",
              fontFamily: brainTokens.font.mono,
              bgcolor: c.panelBg,
              borderRadius: "8px",
              p: 2,
              minHeight: 300,
              overflowX: "auto",
            }}
          >
            {content || "…"}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ fontSize: 12.5, color: c.muted2, mb: "14px" }}>
        Live from the company skills registry (mod-skills → Bedrock AgentCore) — approved skills sync here on
        merge to main.{" "}
        <Box component="a" href={REPO_URL} target="_blank" sx={{ color: c.tealInk2 }}>
          Contribute on GitLab →
        </Box>
      </Box>
      {skills.length === 0 ? (
        <EmptyState icon="hex" title="No skills in the registry yet">
          Skills published from mod-skills will appear here automatically.
        </EmptyState>
      ) : (
        Object.entries(
          skills.reduce<Record<string, CompanySkill[]>>((by, s) => {
            (by[s.category ?? "general"] ??= []).push(s);
            return by;
          }, {}),
        )
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, group]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "10px" }}>
                <Box sx={{ fontSize: 13, fontWeight: 600, fontFamily: brainTokens.font.mono, color: c.tealInk2 }}>
                  {category}
                </Box>
                <Box sx={{ fontSize: 11.5, color: c.faint }}>{group.length}</Box>
                <Box sx={{ flex: 1, borderBottom: `1px solid ${c.border}` }} />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "12px" }}>
                {group.map((s) => (
            <Box
              key={s.name}
              onClick={() => setSelected(s)}
              sx={{
                background: "#fff",
                border: `1px solid ${c.border}`,
                borderRadius: "8px",
                p: "14px 16px",
                cursor: "pointer",
                "&:hover": { borderColor: c.teal },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "6px" }}>
                <Box sx={{ fontSize: 14.5, fontWeight: 600, fontFamily: brainTokens.font.mono }}>{s.name}</Box>
                <Box sx={{ flex: 1 }} />
                {s.syncedAt && (
                  <Box sx={{ fontSize: 11, color: c.faint, fontFamily: brainTokens.font.mono }}>
                    {relDate(Date.parse(s.syncedAt))}
                  </Box>
                )}
              </Box>
              <Box sx={{ fontSize: 13, color: c.muted, lineHeight: 1.5, mb: "8px" }}>
                {s.description ?? "No description."}
              </Box>
              <Box sx={{ fontSize: 11.5, color: c.muted2 }}>{s.files.length} file{s.files.length === 1 ? "" : "s"}</Box>
            </Box>
                ))}
              </Box>
            </Box>
          ))
      )}
    </Box>
  );
}
